window.SMSFarmStorage = (() => {
  "use strict";

  const MODULE_VERSION = "1.2.0";
  const STORAGE_FORMAT_VERSION = 1;
  const CURRENT_SCHEMA_VERSION = 1;

  const STORAGE_KEY = "sms:farm-profile";
  const BACKUP_KEY = `${STORAGE_KEY}:backup`;
  const LOCK_KEY = `${STORAGE_KEY}:lock`;
  const QUARANTINE_KEY = `${STORAGE_KEY}:quarantine`;

  const LOCK_TTL_MS = 10000;
  const LOCK_WAIT_MS = 2500;
  const LOCK_RETRY_MS = 40;
/* --------------------------------------------------------------------------
 * Map storage
 *
 * Maps are stored separately in IndexedDB because File/Blob objects should
 * not be embedded inside the JSON farm profile. These names intentionally
 * match the original My Block map store so existing test maps remain
 * available.
 * ----------------------------------------------------------------------- */

const MAP_DB_NAME = "smsMyBlockDatabaseV2";
const MAP_DB_VERSION = 1;
const MAP_STORE_NAME = "maps";
const MAX_MAP_FILE_SIZE_BYTES = 8 * 1024 * 1024;

const ALLOWED_MAP_TYPES = new Set([
  "overall",
  "winter",
  "spring",
  "summer",
  "autumn"
]);

const ALLOWED_MAP_MIME_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/webp"
]);
  const SEASONS = Object.freeze([
    "winter",
    "spring",
    "summer",
    "autumn"
  ]);

  const instanceId =
    `sms-${Date.now().toString(36)}-` +
    Math.random().toString(36).slice(2, 10);

  let cache = null;
  let debugEnabled = false;
  let localLockHeld = false;

  const migrations = Object.create(null);

  function isObject(value) {
    return (
      value !== null &&
      typeof value === "object" &&
      !Array.isArray(value)
    );
  }

  function clone(value) {
    if (value === undefined) return undefined;

    if (typeof structuredClone === "function") {
      return structuredClone(value);
    }

    return JSON.parse(JSON.stringify(value));
  }

  function now() {
    return new Date().toISOString();
  }

  function validDate(value) {
    return (
      typeof value === "string" &&
      value.trim() !== "" &&
      !Number.isNaN(Date.parse(value))
    );
  }

  function sleep(ms) {
    return new Promise(resolve => {
      window.setTimeout(resolve, ms);
    });
  }

  function log(level, message, detail) {
    if (
      level === "debug" &&
      !debugEnabled
    ) {
      return;
    }

    const prefix = "[SMSFarmStorage]";

    const fn =
      level === "error"
        ? console.error
        : level === "warn"
          ? console.warn
          : level === "info"
            ? console.info
            : console.log;

    fn(prefix, message, detail ?? "");
  }

  function result(success, options = {}) {
    return {
      success: Boolean(success),
      data: options.data ?? null,
      reason: options.reason ?? null,

      errors: Array.isArray(options.errors)
        ? options.errors
        : options.errors
          ? [options.errors]
          : [],

      warnings: Array.isArray(options.warnings)
        ? options.warnings
        : options.warnings
          ? [options.warnings]
          : [],

      detail: options.detail ?? null
    };
  }

  function storageAvailable() {
    try {
      const key = `${STORAGE_KEY}:test`;

      localStorage.setItem(key, "1");
      localStorage.removeItem(key);

      return true;
    } catch (_) {
      return false;
    }
  }

  function readRaw(key) {
    if (!storageAvailable()) {
      return result(false, {
        reason: "storage_unavailable",
        errors:
          "Browser storage is unavailable."
      });
    }

    try {
      const value =
        localStorage.getItem(key);

      return value === null
        ? result(false, {
            reason: "not_found"
          })
        : result(true, {
            data: value
          });
    } catch (error) {
      return result(false, {
        reason: "read_failed",
        errors: error.message,
        detail: error
      });
    }
  }

  function writeRaw(key, value) {
    if (!storageAvailable()) {
      throw new Error(
        "Browser storage is unavailable. " +
        "Check that cookies and site data are allowed."
      );
    }

    localStorage.setItem(key, value);
  }

  function removeRaw(key) {
    if (!storageAvailable()) {
      return false;
    }

    localStorage.removeItem(key);

    return true;
  }

  function parseRaw(raw, label) {
    try {
      return result(true, {
        data: JSON.parse(raw)
      });
    } catch (error) {
      log(
        "error",
        `Invalid JSON in ${label}.`,
        error
      );

      return result(false, {
        reason: "invalid_json",
        errors:
          `Invalid JSON in ${label}.`,
        detail: error
      });
    }
  }

  function readRecord(key, label) {
    const raw = readRaw(key);

    return raw.success
      ? parseRaw(raw.data, label)
      : raw;
  }

  function writeRecord(key, value) {
    writeRaw(
      key,
      JSON.stringify(value)
    );
  }

  function merge(target, source) {
    const output = isObject(target)
      ? clone(target)
      : {};

    if (!isObject(source)) {
      return output;
    }

    Object.keys(source).forEach(key => {
      const incoming = source[key];

      if (incoming === undefined) {
        return;
      }

      output[key] =
        isObject(incoming) &&
        isObject(output[key])
          ? merge(
              output[key],
              incoming
            )
          : clone(incoming);
    });

    return output;
  }

  function createEmptyProfile(
    timestamp = now()
  ) {
    return {
      myBlock: {},

      seasonalPlans: {
        winter: {},
        spring: {},
        summer: {},
        autumn: {}
      },

      recommendationHistory: [],

      settings: {},

      metadata: {
        profileSchemaVersion:
          CURRENT_SCHEMA_VERSION,

        storageFormatVersion:
          STORAGE_FORMAT_VERSION,

        createdAt: timestamp,
        updatedAt: timestamp
      },

      engines: {}
    };
  }

  function normaliseProfile(
    profile,
    timestamp = now()
  ) {
    const source = isObject(profile)
      ? clone(profile)
      : {};

    const createdAt =
      validDate(
        source?.metadata?.createdAt
      )
        ? source.metadata.createdAt
        : timestamp;

    const output = merge(
      createEmptyProfile(createdAt),
      source
    );

    if (!isObject(output.myBlock)) {
      output.myBlock = {};
    }

    if (
      !isObject(output.seasonalPlans)
    ) {
      output.seasonalPlans = {};
    }

    SEASONS.forEach(season => {
      if (
        !isObject(
          output.seasonalPlans[season]
        )
      ) {
        output.seasonalPlans[season] = {};
      }
    });

    if (
      !Array.isArray(
        output.recommendationHistory
      )
    ) {
      output.recommendationHistory = [];
    }

    if (!isObject(output.settings)) {
      output.settings = {};
    }

    if (!isObject(output.metadata)) {
      output.metadata = {};
    }

    if (!isObject(output.engines)) {
      output.engines = {};
    }

    output.metadata.profileSchemaVersion =
      Number.isInteger(
        output.metadata
          .profileSchemaVersion
      )
        ? output.metadata
            .profileSchemaVersion
        : 1;

    output.metadata.storageFormatVersion =
      STORAGE_FORMAT_VERSION;

    output.metadata.createdAt =
      createdAt;

    output.metadata.updatedAt =
      validDate(
        output.metadata.updatedAt
      )
        ? output.metadata.updatedAt
        : timestamp;

    return output;
  }

  function validateProfile(profile) {
    const errors = [];
    const warnings = [];

    if (!isObject(profile)) {
      return {
        valid: false,
        errors: [
          "Farm profile must be an object."
        ],
        warnings
      };
    }

    if (!isObject(profile.myBlock)) {
      errors.push(
        "myBlock must be an object."
      );
    }

    if (
      !isObject(profile.seasonalPlans)
    ) {
      errors.push(
        "seasonalPlans must be an object."
      );
    } else {
      SEASONS.forEach(season => {
        if (
          !isObject(
            profile.seasonalPlans[season]
          )
        ) {
          errors.push(
            `seasonalPlans.${season} must be an object.`
          );
        }
      });
    }

    if (
      !Array.isArray(
        profile.recommendationHistory
      )
    ) {
      errors.push(
        "recommendationHistory must be an array."
      );
    }

    if (!isObject(profile.settings)) {
      errors.push(
        "settings must be an object."
      );
    }

    if (!isObject(profile.engines)) {
      errors.push(
        "engines must be an object."
      );
    }

    if (!isObject(profile.metadata)) {
      errors.push(
        "metadata must be an object."
      );
    } else {
      if (
        !Number.isInteger(
          profile.metadata
            .profileSchemaVersion
        )
      ) {
        errors.push(
          "metadata.profileSchemaVersion must be an integer."
        );
      }

      if (
        profile.metadata
          .profileSchemaVersion >
        CURRENT_SCHEMA_VERSION
      ) {
        errors.push(
          "Profile schema is newer than this storage module supports."
        );
      }

      if (
        !validDate(
          profile.metadata.createdAt
        )
      ) {
        errors.push(
          "metadata.createdAt is invalid."
        );
      }

      if (
        !validDate(
          profile.metadata.updatedAt
        )
      ) {
        errors.push(
          "metadata.updatedAt is invalid."
        );
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  function migrateProfile(profile) {
    let working =
      normaliseProfile(profile);

    let version =
      working.metadata
        .profileSchemaVersion;

    const applied = [];

    if (
      version >
      CURRENT_SCHEMA_VERSION
    ) {
      return result(false, {
        reason: "future_schema",

        errors:
          `Schema ${version} is newer than ` +
          `supported schema ${CURRENT_SCHEMA_VERSION}.`
      });
    }

    while (
      version <
      CURRENT_SCHEMA_VERSION
    ) {
      const migrate =
        migrations[version];

      if (
        typeof migrate !== "function"
      ) {
        return result(false, {
          reason: "migration_missing",

          errors:
            `No migration exists from ` +
            `schema ${version}.`
        });
      }

      working =
        migrate(clone(working));

      if (!isObject(working)) {
        return result(false, {
          reason: "migration_failed",

          errors:
            `Migration from schema ` +
            `${version} returned invalid data.`
        });
      }

      working.metadata ||= {};

      working.metadata
        .profileSchemaVersion =
        version + 1;

      applied.push({
        from: version,
        to: version + 1
      });

      version += 1;
    }

    working =
      normaliseProfile(working);

    working.metadata
      .profileSchemaVersion =
      CURRENT_SCHEMA_VERSION;

    const validation =
      validateProfile(working);

    return validation.valid
      ? result(true, {
          data: working,
          warnings:
            validation.warnings,

          detail: {
            applied
          }
        })
      : result(false, {
          reason: "migration_invalid",
          errors: validation.errors,
          warnings:
            validation.warnings
        });
  }

  function quarantine(raw, reason) {
    if (
      typeof raw !== "string" ||
      raw === ""
    ) {
      return;
    }

    try {
      writeRecord(
        QUARANTINE_KEY,
        {
          reason,
          quarantinedAt: now(),
          raw
        }
      );
    } catch (error) {
      log(
        "warn",
        "Unable to quarantine invalid storage data.",
        error
      );
    }
  }

  function prepareStored(key, label) {
    const raw = readRaw(key);

    if (!raw.success) {
      return raw;
    }

    const parsed =
      parseRaw(raw.data, label);

    if (!parsed.success) {
      quarantine(
        raw.data,
        `${label}: invalid_json`
      );

      return parsed;
    }

    const migrated =
      migrateProfile(parsed.data);

    if (!migrated.success) {
      quarantine(
        raw.data,
        `${label}: ${migrated.reason}`
      );
    }

    return migrated;
  }

  function readLock() {
    const lock = readRecord(
      LOCK_KEY,
      "storage lock"
    );

    return (
      lock.success &&
      isObject(lock.data)
    )
      ? lock.data
      : null;
  }

  function lockExpired(lock) {
    return (
      !lock ||
      !validDate(lock.expiresAt) ||
      Date.parse(lock.expiresAt) <=
        Date.now()
    );
  }

  function tryLock() {
    if (localLockHeld) {
      return false;
    }

    const existing = readLock();

    if (
      existing &&
      !lockExpired(existing) &&
      existing.owner !== instanceId
    ) {
      return false;
    }

    const lock = {
      owner: instanceId,
      acquiredAt: now(),

      expiresAt: new Date(
        Date.now() + LOCK_TTL_MS
      ).toISOString()
    };

    writeRecord(
      LOCK_KEY,
      lock
    );

    const confirmed =
      readLock();

    if (
      !confirmed ||
      confirmed.owner !== instanceId
    ) {
      return false;
    }

    localLockHeld = true;

    log(
      "debug",
      "Transaction lock acquired.",
      lock
    );

    return true;
  }

  async function acquireLock(
    maxWaitMs = LOCK_WAIT_MS
  ) {
    const started = Date.now();

    while (
      Date.now() - started <=
      maxWaitMs
    ) {
      if (tryLock()) {
        return true;
      }

      await sleep(LOCK_RETRY_MS);
    }

    return false;
  }

  function releaseLock() {
    const existing = readLock();

    if (
      existing?.owner === instanceId
    ) {
      removeRaw(LOCK_KEY);
    }

    localLockHeld = false;

    log(
      "debug",
      "Transaction lock released."
    );
  }

  function dispatch(name, detail) {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: clone(detail)
      })
    );
  }

  function dispatchChange(
    action,
    profile,
    context = {}
  ) {
    const detail = {
      action,
      profile: clone(profile),
      context: clone(context),

      timestamp:
        context.timestamp || now(),

      moduleVersion:
        MODULE_VERSION,

      profileSchemaVersion:
        CURRENT_SCHEMA_VERSION
    };

    [
      "sms:farm-profile-changed",
      "sms:profile-updated",
      "sms:data-changed"
    ].forEach(name => {
      dispatch(name, detail);
    });

    const event = {
      create: "sms:farm-created",
      load: "sms:farm-loaded",
      save: "sms:farm-saved",
      update: "sms:farm-saved",
      transaction: "sms:farm-saved",
      import: "sms:farm-saved",
      clear: "sms:farm-cleared",
      recovery: "sms:farm-restored",

      "restore-backup":
        "sms:farm-restored"
    }[action];

    if (event) {
      dispatch(event, detail);
    }
  }

  function loadStoredProfile() {
    const primary =
      prepareStored(
        STORAGE_KEY,
        "primary farm profile"
      );

    if (primary.success) {
      return {
        profile: primary.data,
        source: "primary",

        migrations:
          primary.detail?.applied || [],

        warnings:
          primary.warnings || []
      };
    }

    const backup =
      prepareStored(
        BACKUP_KEY,
        "backup farm profile"
      );

    if (backup.success) {
      log(
        "warn",
        "Primary profile unavailable. Recovering from backup."
      );

      return {
        profile: backup.data,
        source: "backup",

        migrations:
          backup.detail?.applied || [],

        warnings: [
          "Recovered from backup.",
          ...(backup.warnings || [])
        ]
      };
    }

    return {
      profile: null,
      source: "empty",
      migrations: [],
      warnings: []
    };
  }

  function get() {
    if (cache) {
      log(
        "debug",
        "Cache hit."
      );

      return clone(cache);
    }

    const loaded =
      loadStoredProfile();

    if (!loaded.profile) {
      cache =
        createEmptyProfile();

      dispatchChange(
        "load",
        cache,
        {
          source: "empty",
          persisted: false
        }
      );

      return clone(cache);
    }

    cache =
      normaliseProfile(
        loaded.profile
      );

    if (
      loaded.source === "backup" ||
      loaded.migrations.length
    ) {
      try {
        writeRecord(
          STORAGE_KEY,
          cache
        );
      } catch (error) {
        log(
          "warn",
          "Recovered profile loaded but could not be written back.",
          error
        );
      }
    }

    dispatchChange(
      loaded.source === "backup"
        ? "recovery"
        : "load",

      cache,
      loaded
    );

    return clone(cache);
  }

  function loadProfile() {
    return get();
  }

  function exists() {
    return (
      Boolean(cache) ||
      readRaw(STORAGE_KEY).success ||
      readRaw(BACKUP_KEY).success
    );
  }

  function refresh() {
    cache = null;

    return get();
  }

  function persist(
    profile,
    action,
    context = {}
  ) {
    const timestamp =
      context.timestamp || now();

    const prepared =
      normaliseProfile(
        profile,
        timestamp
      );

    prepared.metadata
      .profileSchemaVersion =
      CURRENT_SCHEMA_VERSION;

    prepared.metadata
      .storageFormatVersion =
      STORAGE_FORMAT_VERSION;

    prepared.metadata.updatedAt =
      timestamp;

    const validation =
      validateProfile(prepared);

    if (!validation.valid) {
      throw new Error(
        "Farm profile validation failed: " +
        validation.errors.join(" ")
      );
    }

    if (
      cache &&
      isObject(cache)
    ) {
      writeRecord(
        BACKUP_KEY,
        cache
      );
    }

    writeRecord(
      STORAGE_KEY,
      prepared
    );

    cache = clone(prepared);

    dispatchChange(
      action,
      prepared,
      {
        ...context,
        timestamp,

        warnings:
          validation.warnings
      }
    );

    return clone(prepared);
  }

  async function transaction(
    mutator,
    context = {}
  ) {
    if (
      typeof mutator !== "function"
    ) {
      throw new TypeError(
        "transaction() requires a function."
      );
    }

    if (!(await acquireLock())) {
      const error = new Error(
        "Farm storage is busy. Please try saving again."
      );

      error.code = "storage_busy";

      throw error;
    }

    try {
      cache = null;

      const current = get();
      const draft = clone(current);

      const returned =
        await mutator(draft);

      const next =
        returned === undefined
          ? draft
          : returned;

      if (!isObject(next)) {
        throw new TypeError(
          "A transaction must return a farm profile object or mutate the supplied draft."
        );
      }

      return persist(
        next,
        context.action ||
          "transaction",
        context
      );
    } finally {
      releaseLock();
    }
  }

  async function set(
    profile,
    context = {}
  ) {
    if (!isObject(profile)) {
      throw new TypeError(
        "set() requires a complete farm profile object."
      );
    }

    return transaction(
      () => clone(profile),

      {
        ...context,

        action:
          context.action ||
          "save"
      }
    );
  }

  async function update(
    partialProfile,
    context = {}
  ) {
    if (!isObject(partialProfile)) {
      throw new TypeError(
        "update() requires a partial farm profile object."
      );
    }

    return transaction(
      current => {
        const next = merge(
          current,
          partialProfile
        );

        if (
          isObject(context.sources)
        ) {
          next.engines ||= {};

          next.engines.sources =
            merge(
              next.engines.sources || {},
              context.sources
            );
        }

        return next;
      },

      {
        ...context,

        action:
          context.action ||
          "update"
      }
    );
  }

  async function create(
    options = {}
  ) {
    const {
      overwrite = false,
      initialProfile = null,
      context = {}
    } = options;

    if (
      !overwrite &&
      exists()
    ) {
      return get();
    }

    const timestamp =
      context.timestamp || now();

    const profile =
      initialProfile
        ? normaliseProfile(
            initialProfile,
            timestamp
          )
        : createEmptyProfile(
            timestamp
          );

    return set(
      profile,
      {
        ...context,
        action: "create",
        timestamp
      }
    );
  }

  async function clear(
    options = {}
  ) {
    const {
      includeBackup = true,
      includeQuarantine = false,
      dispatchEvent = true
    } = options;

    if (!(await acquireLock())) {
      const error = new Error(
        "Farm storage is busy. Please try again."
      );

      error.code = "storage_busy";

      throw error;
    }

    try {
      removeRaw(STORAGE_KEY);

      if (includeBackup) {
        removeRaw(BACKUP_KEY);
      }

      if (includeQuarantine) {
        removeRaw(
          QUARANTINE_KEY
        );
      }

      cache = null;

      if (dispatchEvent) {
        dispatchChange(
          "clear",
          createEmptyProfile(),
          {
            timestamp: now()
          }
        );
      }

      return true;
    } finally {
      releaseLock();
    }
  }

  function inspect(key, label) {
    const raw = readRaw(key);

    if (!raw.success) {
      return {
        exists: false,
        validJson: false,
        validProfile: false,
        reason: raw.reason,
        errors: raw.errors
      };
    }

    const parsed =
      parseRaw(raw.data, label);

    if (!parsed.success) {
      return {
        exists: true,
        validJson: false,
        validProfile: false,
        reason: parsed.reason,
        errors: parsed.errors
      };
    }

    const migrated =
      migrateProfile(parsed.data);

    if (!migrated.success) {
      return {
        exists: true,
        validJson: true,
        validProfile: false,
        reason: migrated.reason,
        errors: migrated.errors
      };
    }

    const validation =
      validateProfile(
        migrated.data
      );

    return {
      exists: true,
      validJson: true,

      validProfile:
        validation.valid,

      reason:
        validation.valid
          ? null
          : "validation_failed",

      errors:
        validation.errors,

      warnings:
        validation.warnings,

      schemaVersion:
        migrated.data.metadata
          .profileSchemaVersion,

      migrationsAvailable:
        migrated.detail?.applied || []
    };
  }

  function health(options = {}) {
    const includeDetails =
      options.includeDetails !== false;

    const errors = [];
    const warnings = [];

    const available =
      storageAvailable();

    const primary =
      available
        ? inspect(
            STORAGE_KEY,
            "primary farm profile"
          )
        : null;

    const backup =
      available
        ? inspect(
            BACKUP_KEY,
            "backup farm profile"
          )
        : null;

    if (!available) {
      errors.push(
        "Browser storage is unavailable."
      );
    }

    if (
      primary?.exists &&
      !primary.validProfile
    ) {
      errors.push(
        "The primary farm profile is invalid."
      );
    }

    if (
      !primary?.exists &&
      backup?.validProfile
    ) {
      warnings.push(
        "Primary profile is missing, but a valid backup exists."
      );
    }

    if (
      primary?.exists &&
      !backup?.exists
    ) {
      warnings.push(
        "No recovery backup exists yet. It will be created after another successful save."
      );
    }

    if (
      backup?.exists &&
      !backup.validProfile
    ) {
      warnings.push(
        "The recovery backup is invalid."
      );
    }

    const lock = readLock();

    const report = {
      healthy:
        errors.length === 0,

      storageAvailable:
        available,

      cacheLoaded:
        Boolean(cache),

      transactionLocked:
        Boolean(
          lock &&
          !lockExpired(lock)
        ),

      moduleVersion:
        MODULE_VERSION,

      profileSchemaVersion:
        CURRENT_SCHEMA_VERSION,

      errors,
      warnings
    };

    if (includeDetails) {
      Object.assign(
        report,
        {
          primary,
          backup
        }
      );
    }

    return report;
  }

  async function repair() {
    const report = health();

    if (!report.storageAvailable) {
      return result(false, {
        reason:
          "storage_unavailable",

        errors:
          report.errors
      });
    }

    const backup =
      prepareStored(
        BACKUP_KEY,
        "backup farm profile"
      );

    if (
      (
        !report.primary?.exists ||
        !report.primary.validProfile
      ) &&
      backup.success
    ) {
      const restored =
        await set(
          backup.data,
          {
            action:
              "restore-backup",

            source:
              "health-repair"
          }
        );

      return result(true, {
        data: restored,

        warnings:
          "Primary profile restored from backup."
      });
    }

    if (
      !report.primary?.exists &&
      !backup.success
    ) {
      const created =
        await create({
          overwrite: true,

          context: {
            source:
              "health-repair"
          }
        });

      return result(true, {
        data: created,

        warnings:
          "A new empty profile was created."
      });
    }

    return result(true, {
      data: get(),
      warnings: report.warnings
    });
  }

  function getBackup() {
    const backup =
      prepareStored(
        BACKUP_KEY,
        "backup farm profile"
      );

    return backup.success
      ? clone(backup.data)
      : null;
  }

  async function restoreBackup(
    context = {}
  ) {
    const backup = getBackup();

    if (!backup) {
      return result(false, {
        reason:
          "backup_not_found",

        errors:
          "No valid backup was found."
      });
    }

    return result(true, {
      data: await set(
        backup,
        {
          ...context,

          action:
            "restore-backup"
        }
      )
    });
  }

  function exportProfile(
    options = {}
  ) {
    return JSON.stringify(
      get(),
      null,
      options.pretty === false
        ? 0
        : 2
    );
  }

  async function importProfile(
    profileOrJson,
    context = {}
  ) {
    let candidate =
      profileOrJson;

    if (
      typeof candidate === "string"
    ) {
      const parsed = parseRaw(
        candidate,
        "imported farm profile"
      );

      if (!parsed.success) {
        return parsed;
      }

      candidate = parsed.data;
    }

    if (!isObject(candidate)) {
      return result(false, {
        reason: "invalid_profile",

        errors:
          "Imported profile must be an object."
      });
    }

    const migrated =
      migrateProfile(candidate);

    if (!migrated.success) {
      return migrated;
    }

    try {
      return result(true, {
        data: await set(
          migrated.data,
          {
            ...context,
            action: "import"
          }
        ),

        warnings:
          migrated.warnings
      });
    } catch (error) {
      return result(false, {
        reason:
          error.code ||
          "import_failed",

        errors:
          error.message,

        detail: error
      });
    }
  }

  function debug(enabled) {
    if (enabled === undefined) {
      return debugEnabled;
    }

    debugEnabled =
      Boolean(enabled);

    log(
      "info",
      `Debug mode ${
        debugEnabled
          ? "enabled"
          : "disabled"
      }.`
    );

    return debugEnabled;
  }

  function getVersion() {
    return {
      moduleVersion:
        MODULE_VERSION,

      storageFormatVersion:
        STORAGE_FORMAT_VERSION,

      profileSchemaVersion:
        CURRENT_SCHEMA_VERSION,

      storageKey:
        STORAGE_KEY,

      backupKey:
        BACKUP_KEY,

      instanceId
    };
  }
/* --------------------------------------------------------------------------
 * Map storage API
 * ----------------------------------------------------------------------- */

function validateMapType(type) {
  const mapType = String(type || "")
    .trim()
    .toLowerCase();

  if (!ALLOWED_MAP_TYPES.has(mapType)) {
    throw new TypeError(
      "Unsupported map type: " +
      (mapType || "(empty)") +
      "."
    );
  }

  return mapType;
}

function openMapDatabase() {
  return new Promise((resolve, reject) => {
    if (!("indexedDB" in window)) {
      reject(
        new Error(
          "IndexedDB is unavailable in this browser."
        )
      );
      return;
    }

    const request = window.indexedDB.open(
      MAP_DB_NAME,
      MAP_DB_VERSION
    );

    request.onupgradeneeded = event => {
      const database = event.target.result;

      if (
        !database.objectStoreNames.contains(
          MAP_STORE_NAME
        )
      ) {
        database.createObjectStore(
          MAP_STORE_NAME,
          {
            keyPath: "type"
          }
        );
      }
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onerror = () => {
      reject(
        request.error ||
        new Error(
          "Could not open the map database."
        )
      );
    };

    request.onblocked = () => {
      reject(
        new Error(
          "The map database is blocked by another open browser tab."
        )
      );
    };
  });
}

async function uploadMap(type, file) {
  const mapType = validateMapType(type);

  if (!(file instanceof Blob)) {
    throw new TypeError(
      "uploadMap() requires an image file."
    );
  }

  if (file.size > MAX_MAP_FILE_SIZE_BYTES) {
    throw new RangeError(
      "Please upload an image under 8 MB."
    );
  }

  if (
    file.type &&
    !ALLOWED_MAP_MIME_TYPES.has(file.type)
  ) {
    throw new TypeError(
      "Maps must be PNG, JPEG or WebP images."
    );
  }

  const database = await openMapDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(
        MAP_STORE_NAME,
        "readwrite"
      );

      transaction
        .objectStore(MAP_STORE_NAME)
        .put({
          type: mapType,
          file,
          fileName: file.name || "",
          fileType: file.type || "",
          fileSize: Number(file.size) || 0,
          updatedAt: Date.now()
        });

      transaction.oncomplete = () => {
        dispatch("sms:farm-map-changed", {
          action: "upload",
          type: mapType,
          timestamp: now()
        });

        resolve(true);
      };

      transaction.onerror = () => {
        reject(
          transaction.error ||
          new Error("Could not save the map.")
        );
      };

      transaction.onabort =
        transaction.onerror;
    });
  } finally {
    database.close();
  }
}

async function loadMap(type) {
  const mapType = validateMapType(type);
  const database = await openMapDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(
        MAP_STORE_NAME,
        "readonly"
      );

      const request = transaction
        .objectStore(MAP_STORE_NAME)
        .get(mapType);

      request.onsuccess = () => {
        resolve(request.result || null);
      };

      request.onerror = () => {
        reject(
          request.error ||
          new Error("Could not load the map.")
        );
      };
    });
  } finally {
    database.close();
  }
}

async function deleteMap(type) {
  const mapType = validateMapType(type);
  const database = await openMapDatabase();

  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction(
        MAP_STORE_NAME,
        "readwrite"
      );

      transaction
        .objectStore(MAP_STORE_NAME)
        .delete(mapType);

      transaction.oncomplete = () => {
        dispatch("sms:farm-map-changed", {
          action: "delete",
          type: mapType,
          timestamp: now()
        });

        resolve(true);
      };

      transaction.onerror = () => {
        reject(
          transaction.error ||
          new Error("Could not remove the map.")
        );
      };

      transaction.onabort =
        transaction.onerror;
    });
  } finally {
    database.close();
  }
}
  function getStorageKey() {
    return STORAGE_KEY;
  }

  window.addEventListener(
    "storage",
    event => {
      if (
        ![
          STORAGE_KEY,
          BACKUP_KEY,
          LOCK_KEY
        ].includes(event.key)
      ) {
        return;
      }

      if (
        [
          STORAGE_KEY,
          BACKUP_KEY
        ].includes(event.key)
      ) {
        cache = null;

        dispatch(
          "sms:farm-external-change",
          {
            key: event.key,
            timestamp: now()
          }
        );
      }
    }
  );

const api = Object.freeze({
  MODULE_VERSION,
  STORAGE_FORMAT_VERSION,
  CURRENT_SCHEMA_VERSION,

    createEmptyProfile,
    normaliseProfile,
    validateProfile,
    migrateProfile,

    get,
    loadProfile,
    set,
    update,
    transaction,
    create,
    exists,
    clear,
    refresh,

    health,
    repair,

    getBackup,
    restoreBackup,

exportProfile,
importProfile,

uploadMap,
loadMap,
deleteMap,

debug,
getVersion,
getStorageKey
  });

  dispatch(
    "sms:farm-storage-ready",
    getVersion()
  );

  return api;
})();